import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Database, Project } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { Trail } from "@/ui/Trail";
import { Button } from "@/ui/Button";
import { StateLine } from "@/ui/Lamp";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { CreateDatabaseDialog } from "@/features/databases/CreateDatabaseDialog";
import { relativeTime, absoluteTime } from "@/lib/format";

export const Route = createFileRoute("/_app/o/$orgId/projects/$projectId/databases/")({
  component: DatabasesScreen,
});

function DatabasesScreen() {
  const { orgId, projectId } = Route.useParams();
  const [creating, setCreating] = useState(false);

  const project = useQuery({
    queryKey: ["project", orgId, projectId],
    queryFn: ({ signal }) =>
      api.get<{ project: Project }>(
        `/organisations/${orgId}/projects/${projectId}`,
        undefined,
        signal,
      ),
  });

  const databases = useQuery({
    queryKey: ["databases", orgId, projectId],
    queryFn: ({ signal }) =>
      api.get<{ databases: Database[] | null }>(
        `/organisations/${orgId}/projects/${projectId}/databases`,
        undefined,
        signal,
      ),
    refetchInterval: (query) =>
      (query.state.data?.databases ?? []).some((d) =>
        ["QUEUED", "DEPLOYING", "STARTING", "DELETING"].includes(d.state),
      )
        ? 4000
        : false,
  });

  const list = databases.data?.databases ?? [];
  const environments = project.data?.project.environments ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Head
        // Trail rather than a "Back to project" button.
        //
        // Every other page nested under a project says where it is with a
        // trail; this one had a ghost button beside the primary action,
        // which both hid the way back among the things you can do and put
        // two controls of different kinds on the same line.
        above={
          <Trail
            trail={[
              { label: "Projects", to: "/o/$orgId/projects", params: { orgId } },
              {
                label: project.data?.project.display_name ?? "Project",
                to: "/o/$orgId/projects/$projectId",
                params: { orgId, projectId },
              },
              { label: "Databases" },
            ]}
          />
        }
        title="Databases"
        description="Managed PostgreSQL for this project. Backed up, patched and kept running for you."
        actions={
          <>
            <Button
              tone="primary"
              onClick={() => setCreating(true)}
              disabled={environments.length === 0}
            >
              New database
            </Button>
          </>
        }
      />

      <CreateDatabaseDialog
        open={creating}
        onOpenChange={setCreating}
        orgId={orgId}
        projectId={projectId}
        environments={environments}
      />

      <Panel padded={false}>
        {databases.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={3} columns={4} />
          </div>
        ) : databases.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={databases.error} onRetry={() => void databases.refetch()} />
          </div>
        ) : list.length === 0 ? (
          /* No button: "New database" is in the page header and stays
             there once one exists. */
          <EmptyState
            title="No databases yet"
            description="A database is created per environment and reachable only from the services in it. Nothing outside your project can connect to it."
          />
        ) : (
          <div className="px-5 py-4">
            <Table>
              <thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>State</Th>
                  <Th>Size</Th>
                  <Th align="right">Created</Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((database) => (
                  <Tr key={database.id}>
                    <Td>
                      <Link
                        to="/o/$orgId/projects/$projectId/databases/$databaseId"
                        params={{ orgId, projectId, databaseId: database.id }}
                        className="font-medium text-[var(--text-primary)] hover:text-[var(--primary)]"
                      >
                        {database.display_name}
                      </Link>
                      <p className="mt-0.5 text-[13px] text-[var(--text-muted)]">
                        PostgreSQL {database.major_version}
                      </p>
                    </Td>
                    <Td>
                      <StateLine state={database.state} />
                      {database.state_reason && (
                        <p className="mt-1 max-w-md text-[13px] text-[var(--text-secondary)]">
                          {database.state_reason}
                        </p>
                      )}
                    </Td>
                    <Td>
                      {Math.round(database.memory_mib / 1024)} GiB memory, {database.storage_gib} GiB
                      storage
                    </Td>
                    <Td align="right" title={absoluteTime(database.created_at)}>
                      {relativeTime(database.created_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}
